import { Component, Inject, OnInit, Optional } from "@angular/core";
import { CommonModule } from "@angular/common";
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from "@angular/material/dialog";
import moment from "moment";
import { InputLabelComponent } from "../input-label/input-label.component";
import {
  ActionDate,
  ActiveStatus,
  ArtefactFilterOptions,
  InstanceTypes,
  UsageCategory,
  UserStatus,
  VisibilityStatus,
} from "src/pages/manage-grievances/constants";
import { DateRangeInputComponent } from "../date-range-input/date-range-input.component";
import { SafeHTMLPipe } from "src/pipes/safe-html.pipe";
import { crossBlackSVG } from "src/constants/svg/cross-black";
import { SharedAppModule } from "src/pages/shared/shared.module";
import { UtilityService } from "src/services/utility.service";
import { SearchableDropdownComponent } from "src/pages/shared/searchable-dropdown/searchable-dropdown.component";
import { OrganizationType } from "src/constants/constants";

@Component({
  selector: "filter-dialog",
  standalone: true,
  imports: [
    CommonModule,
    SharedAppModule,
    MatDialogModule,
    ReactiveFormsModule,
    InputLabelComponent,
    DateRangeInputComponent,
  ],
  templateUrl: "./filter-dialog.component.html",
  styleUrls: ["./filter-dialog.component.scss"],
})
export class FilterDialogComponent implements OnInit {
  filterForm!: FormGroup;
  customDateRange!: FormGroup;
  appliedFilterCount = 0;
  readonly crossBlackSVG = crossBlackSVG;
  readonly ActionDate = ActionDate;
  readonly UserStatus = UserStatus;
  readonly VisibilityStatus = VisibilityStatus;
  readonly ActiveStatus = ActiveStatus;
  readonly InstanceTypes = InstanceTypes;
  readonly UsageCategory = UsageCategory;

  readonly instanceTypesLabels = {
    [InstanceTypes.CPU]: "CPU",
    [InstanceTypes.GPU]: "GPU",
  };

  readonly usageCategoryLabels = {
    [UsageCategory.Basic]: "Basic",
    [UsageCategory.Advance]: "Advance",
  };
  appliedFilters: ArtefactFilterOptions;
  dataConfig: Record<string, boolean> = {};

  artifactTypeList = [
    { key: "Dataset", value: "dataset", label: "Dataset" },
    { key: "Model", value: "model", label: "Model" },
    { key: "Usecase", value: "usecase", label: "usecase" },
  ];

  categoryTypeList = [
    {
      key: "Accessibility & Usability",
      value: "17",
      label: "Accessibility & Usability",
    },
    {
      key: "Artefact Upload and download issues",
      value: "15",
      label: "Artefact Upload and download issues",
    },
    {
      key: "Organization & Team Management",
      value: "14",
      label: "Organization & Team Management",
    },
    {
      key: "Profile & Account Issues",
      value: "13",
      label: "Profile & Account Issues",
    },
    {
      key: "Report a Artefact",
      value: "12",
      label: "Report a Artefact",
    },

    {
      key: "Technical Issues",
      value: "16",
      label: "Technical Issues",
    },
    { key: "Others", value: "18", label: "Others" },
  ];

  organizationTypeList!: { key: string; value: string; label: string }[];

  readonly actionDateLabels = {
    [ActionDate.last7Days]: "Last 7 days",
    [ActionDate.last15Days]: "Last 15 days",
    [ActionDate.custom]: "Custom",
  };

  readonly userStatusLabels = {
    [UserStatus.active]: "Active",
    [UserStatus.blocked]: "Blocked",
    [UserStatus.inactive]: "Inactive",
  };

  readonly visibilityStatusLabels = {
    [VisibilityStatus.open]: "Open",
    [VisibilityStatus.restricted]: "Restricted",
    [VisibilityStatus.private]: "Private",
    [VisibilityStatus.shared]: "Shared",
  };

  readonly activeStatusLabels = {
    [ActiveStatus.hidden]: "Hidden",
    [ActiveStatus.active]: "Active",
  };

  readonly requestedBy = [];

  constructor(
    @Optional()
    private dialogRef: MatDialogRef<FilterDialogComponent> | null,
    public utilityService: UtilityService,
    @Inject(MAT_DIALOG_DATA)
    public dialogData?: { filters: ArtefactFilterOptions; config: any }
  ) {
    this.dataConfig = dialogData?.config;
    this.appliedFilters = dialogData?.filters ?? ({} as ArtefactFilterOptions);
    if (dialogRef) dialogRef.disableClose = true;

    this.organizationTypeList = OrganizationType.map(
      (item) => ({
        key: item,
        value: item,
        label: item,
      })
    );
  }

  ngOnInit() {
    this.dialogRef?.addPanelClass("artefact-filter-dialog");
    this.initFilterForm();
  }

  private initFilterForm() {
    this.filterForm = new FormGroup({
      dateFilterType: new FormControl(null),
      artifactType: new FormControl(null),
      category: new FormControl(null),
      userStatus: new FormControl(null),
      visibility: new FormControl(null),
      isActive: new FormControl(null),
      organizationType: new FormControl(null),
      instanceTypes: new FormControl(null),
      usageCategory: new FormControl(null),
    });

    this.customDateRange = new FormGroup({
      fromDate: new FormControl("", [Validators.required]),
      toDate: new FormControl("", [Validators.required]),
    });

    this.prefillFilterValues();
  }

  private prefillFilterValues() {
    const {
      actionDate,
      dateFilterType,
      category = [],
      artifactType = [],
      userStatusType,
      visibility,
      isActive,
      organizationType = [],
      instanceTypes,
      usageCategory,
    } = this.appliedFilters || {};

    this.filterForm.patchValue({
      dateFilterType: dateFilterType?.value,
      category: category?.map(({ value }) => value),
      artifactType: artifactType?.map(({ value }) => value),
      userStatus: userStatusType?.value,
      visibility: visibility?.value,
      isActive: isActive?.value,
      organizationType: organizationType?.map(({ value }) => value),
      instanceTypes: instanceTypes?.value,
      usageCategory: usageCategory?.value,
    });
    if (actionDate) {
      const { fromDate, toDate } = actionDate;

      this.customDateRange.setValue({
        fromDate: moment.isMoment(fromDate)
          ? fromDate.toDate()
          : new Date(fromDate),
        toDate: moment.isMoment(toDate) ? toDate.toDate() : new Date(toDate),
      });
    }
  }

  setActionDate(value: ActionDate) {
    this.filterForm.get("dateFilterType")!.setValue(value);

    if (value !== ActionDate.custom) {
      this.customDateRange.reset();
    }
  }

  setUserStatus(value: UserStatus) {
    this.filterForm.get("userStatus")!.setValue(value);
  }

  setVisibility(value: VisibilityStatus) {
    this.filterForm.get("visibility")!.setValue([value]);
  }

  setActiveStatus(value: ActiveStatus) {
    this.filterForm.get("isActive")!.setValue(value);
  }

  onClearFilters() {
    this.filterForm.reset();
    this.customDateRange.reset();
  }

  onApplyFilters() {
    const {
      category,
      artifactType,
      dateFilterType,
      userStatus,
      visibility,
      isActive,
      organizationType,
      instanceTypes,
      usageCategory,
    } = this.filterForm.value;

    if (dateFilterType === ActionDate.custom && this.customDateRange.invalid) {
      return;
    }

    let selectedValues: ArtefactFilterOptions = {
      dateFilterType: {
        label: this.actionDateLabels[dateFilterType as ActionDate] || "",
        value: dateFilterType || "",
      },
      userStatusType: {
        label: this.userStatusLabels[userStatus as UserStatus] || "",
        value: userStatus || "",
      },
      visibility: {
        label:
          this.visibilityStatusLabels[visibility as VisibilityStatus] || "",
        value: visibility || "",
      },
      isActive: {
        label: this.activeStatusLabels[isActive as ActiveStatus] || "",
        value: isActive || "",
      },
      actionDate: this.getActionDate(dateFilterType),
      category:
        category?.map((item: string) => ({
          label: this.categoryTypeList.find(({ value }) => item === value)!
            .label,
          value: item,
        })) || [],
      organizationType:
        organizationType?.map((item: string) => ({
          label: this.organizationTypeList.find(({ value }) => item === value)!.label,
          value: item,
        })) || [],
      artifactType:
        artifactType?.map((item: string) => ({
          label: this.artifactTypeList.find(({ value }) => item === value)!
            .label,
          value: item,
        })) || [],
      instanceTypes: instanceTypes
        ? {
            label: this.instanceTypesLabels[instanceTypes as InstanceTypes] || "",
            value: instanceTypes || "",
          }
        : undefined,
      usageCategory: usageCategory
        ? {
            label:
              this.usageCategoryLabels[usageCategory as UsageCategory] || "",
            value: usageCategory || "",
          }
        : undefined,
    };

    if (this.dialogRef) {
      selectedValues = {
        ...selectedValues,
        actionDate: this.handleDateFormat(selectedValues?.actionDate),
      };
      this.dialogRef.close(selectedValues);
    }
  }

  getActionDate(dateFilterType: ActionDate | undefined): any {
    if (!dateFilterType) {
      return undefined; // Or a default value if needed
    }

    if (dateFilterType !== ActionDate.custom) {
      return this.handleActionDate(dateFilterType);
    }

    return this.customDateRange.value;
  }

  handleActionDate(actionDate: string) {
    const toDate = new Date();
    let fromDate = new Date();

    if (actionDate === ActionDate.last7Days) {
      fromDate.setDate(fromDate.getDate() - 7);
    } else if (actionDate === ActionDate.last15Days) {
      fromDate.setDate(fromDate.getDate() - 15);
    }

    return { toDate, fromDate };
  }

  handleDateFormat(actionDate: any) {
    return {
      fromDate: this.formatDate(actionDate?.fromDate) as string, // DD/MM/YYYY
      toDate: this.formatDate(actionDate?.toDate) as string, // DD/MM/YYYY
    };
  }

  formatDate = (date: string | moment.Moment | Date | undefined) => {
    if (!date) return undefined;
    return moment(date).format("YYYY-MM-DD");
  };

  setInstanceTypes(value: InstanceTypes) {
    this.filterForm.get("instanceTypes")!.setValue(value);

    if (value === InstanceTypes.CPU) {
      this.filterForm.get("usageCategory")!.reset();
    }
  }

  setUsageCategory(value: UsageCategory) {
    this.filterForm.get("usageCategory")!.setValue(value);
  }

  isApplyDisabled(): boolean {
    if (!this.dataConfig?.disableApplyUntilFilterSelected) {
      return false;
    }

    const formValues = this.filterForm?.value;

    const hasFormValue = Object.values(formValues || {}).some((value: any) => {
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      return value !== null && value !== "" && value !== undefined;
    });

    const hasCustomDate =
      this.filterForm.get("dateFilterType")?.value === ActionDate.custom &&
      this.customDateRange.valid;

    return !(hasFormValue || hasCustomDate);
  }
}
